import React, { useState, useEffect } from 'react';
import { getData } from '../../services/api.service';
import { toastError, toastSuccess } from '../common/Toast';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaTimes, FaDownload } from 'react-icons/fa';
import FormFilterDate from '../common/FormFilterDate';

/**
 * Modal para mostrar tabla de producción con todos los pedidos
 * Solo accesible para Admin, Finanzas y Taller
 */
function ProductionTableModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dateFilters, setDateFilters] = useState(null);

  useEffect(() => {
    if (isOpen && !dateFilters) {
      // No cargar automáticamente, esperar que el usuario seleccione fechas
      setRows([]);
    }
  }, [isOpen]);

  const loadProductionData = async (filters = null) => {
    try {
      setLoading(true);
      
      let url = '/api/orders/production-table';
      
      // Agregar parámetros de fecha si existen
      if (filters?.date_from || filters?.date_to) {
        const params = new URLSearchParams();
        if (filters.date_from) params.append('start_date', filters.date_from);
        if (filters.date_to) params.append('end_date', filters.date_to);
        url += `?${params.toString()}`;
      }
      
      const response = await getData(url, true);
      
      if (response?.data?.rows) {
        setRows(response.data.rows);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Error cargando datos de producción:', error);
      toastError('Error al cargar los datos de producción');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = (data) => {
    setDateFilters(data);
    loadProductionData(data);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  const calculateEstimatedDelivery = (createdDate, estimatedDays) => {
    if (!createdDate || !estimatedDays) return 'N/A';
    
    const created = new Date(createdDate);
    let daysToAdd = estimatedDays;
    let currentDate = new Date(created);
    
    while (daysToAdd > 0) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      // Contar solo días hábiles (lunes a viernes)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysToAdd--;
      }
    }
    
    return currentDate.toLocaleDateString('es-AR');
  };

  const groupByOrder = () => {
    const grouped = {};
    rows.forEach(row => {
      if (!grouped[row.order_id]) {
        grouped[row.order_id] = [];
      }
      grouped[row.order_id].push(row);
    });
    return grouped;
  };

  const groupedRows = groupByOrder();

  const exportToExcel = () => {
    try {
      const excelData = rows.map(row => ({
        'ID PEDIDO': `#${row.order_id}`,
        'MODELO': row.sku_name || 'N/A',
        'CANTIDAD': row.quantity || 0,
        'MEDIDA': row.measurements || 'N/A',
        'COLOR': row.color_names || 'N/A',
        'MATERIAL (TELA)': row.material_name || 'N/A',
        'OBSERVACIONES': row.observations || '',
        'FECHA CREACIÓN': formatDate(row.inserted_date),
        'FECHA ESTIMADA ENTREGA': calculateEstimatedDelivery(row.inserted_date, row.estimated_delivery_days),
        'TIPO ENTREGA': row.delivery_type_name || 'N/A'
      }));

      // Crear workbook y worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 12 },  // ID PEDIDO
        { wch: 30 },  // MODELO
        { wch: 10 },  // CANTIDAD
        { wch: 20 },  // MEDIDA
        { wch: 25 },  // COLOR
        { wch: 25 },  // MATERIAL
        { wch: 40 },  // OBSERVACIONES
        { wch: 18 },  // FECHA CREACIÓN
        { wch: 22 },  // FECHA ESTIMADA ENTREGA
        { wch: 25 }   // TIPO ENTREGA
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pedidos Producción');

      // Generar archivo
      const fileName = `Tabla_Produccion_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toastSuccess('Archivo Excel generado exitosamente');
    } catch (error) {
      console.error('Error generando Excel:', error);
      toastError('Error al generar el archivo Excel');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <FaFileExcel className="me-2 text-success" />
              Tabla de Producción - Todos los Pedidos
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Filtro de fechas */}
            <div className="mb-3">
              <FormFilterDate handleInfo={handleDateFilter} />
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <div className="mt-2">Cargando pedidos...</div>
              </div>
            ) : rows.length === 0 && !dateFilters ? (
              <div className="alert alert-info">
                <strong>ℹ️ Información:</strong> Seleccione un rango de fechas para cargar los pedidos.
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm table-hover">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>ID</th>
                        <th>MODELO</th>
                        <th>CANT.</th>
                        <th>MEDIDA</th>
                        <th>COLOR</th>
                        <th>MATERIAL</th>
                        <th>OBSERVACIONES</th>
                        <th>FECHA CREACIÓN</th>
                        <th>FECHA EST. ENTREGA</th>
                        <th>TIPO ENTREGA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center text-muted py-4">
                            No hay pedidos registrados
                          </td>
                        </tr>
                      ) : (
                        Object.entries(groupedRows).map(([orderId, orderRows]) => (
                          orderRows.map((row, idx) => (
                            <tr key={`${row.order_id}-${row.item_id}`}>
                              {idx === 0 && (
                                <td rowSpan={orderRows.length} className="align-middle">
                                  <strong>#{row.order_id}</strong>
                                </td>
                              )}
                              <td>
                                <small>{row.sku_name || 'N/A'}</small>
                              </td>
                              <td className="text-center">
                                <strong>{row.quantity || 0}</strong>
                              </td>
                              <td>
                                <small>{row.measurements || 'N/A'}</small>
                              </td>
                              <td>
                                <small>{row.color_names || 'N/A'}</small>
                              </td>
                              <td>
                                <small>{row.material_name || 'N/A'}</small>
                              </td>
                              {idx === 0 && (
                                <>
                                  <td rowSpan={orderRows.length} className="align-middle">
                                    <small>{row.observations || '-'}</small>
                                  </td>
                                  <td rowSpan={orderRows.length} className="align-middle">
                                    <small>{formatDate(row.inserted_date)}</small>
                                  </td>
                                  <td rowSpan={orderRows.length} className="align-middle">
                                    <small>{calculateEstimatedDelivery(row.inserted_date, row.estimated_delivery_days)}</small>
                                  </td>
                                  <td rowSpan={orderRows.length} className="align-middle">
                                    <small>{row.delivery_type_name || 'N/A'}</small>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="alert alert-info mt-3">
                  <strong>📊 Total de pedidos:</strong> {Object.keys(groupedRows).length}
                  <br />
                  <strong>📦 Total de items:</strong> {rows.length}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              <FaTimes className="me-2" />
              Cerrar
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={exportToExcel}
              disabled={loading || rows.length === 0}
            >
              <FaDownload className="me-2" />
              Descargar Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductionTableModal;
